import * as THREE from "three";

/**
 * Anisotropic filtering level for the procedural maps.
 *
 * Three clamps this to whatever the GPU supports
 * (`min(texture.anisotropy, capabilities.getMaxAnisotropy())`), so naming the
 * hardware ceiling rather than a portable-looking number costs nothing on a
 * weaker GPU and buys the full amount on one that has it. 16 is where the
 * `EXT_texture_filter_anisotropic` limit sits on essentially everything
 * current, so going higher would be clamped straight back down.
 *
 * It matters here because these patterns are minified hard — the aluminium
 * grooves repeat once per 90 mm of a part that can be half a metre long — and
 * without it a groove or a leather grain breaks into dashes at distance and
 * crawls while the camera orbits. The grooves are also the worst case for it:
 * they run along the extrusion, so a profile seen down its length is exactly
 * the grazing angle isotropic filtering handles worst.
 *
 * Only takes effect on a mipmapped minification filter — three skips the
 * `texParameterf` entirely otherwise, which is what {@link
 * createProceduralTexture} guarantees.
 */
const PROCEDURAL_ANISOTROPY = 16;

/**
 * Repeating, mipmapped canvas texture.
 *
 * Mipmaps are the reason for this helper: setting `minFilter` to a plain
 * `LinearFilter` — as this code used to — silently switches mipmapping off, so
 * every minified sample is a point sample of a high-frequency pattern.
 */
function createProceduralTexture(
  canvas: HTMLCanvasElement,
): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = PROCEDURAL_ANISOTROPY;
  return texture;
}

/**
 * Texel dimensions of a bake. A bare number is a square; a pattern that only
 * varies along one axis can ask for a strip instead and spend its texels
 * where the detail is.
 */
export type TextureExtent =
  | number
  | Readonly<{ width: number; height: number }>;

export function textureExtent(extent: TextureExtent): {
  width: number;
  height: number;
} {
  return typeof extent === "number"
    ? { width: extent, height: extent }
    : { width: extent.width, height: extent.height };
}

/** Bake a height field into a repeating canvas normal map. */
export function heightMapToNormalMap(
  extent: TextureExtent,
  heightMap: Float32Array,
  intensity: number,
): THREE.CanvasTexture {
  const { width, height } = textureExtent(extent);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context for procedural normal-map");
  }

  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;

  const getHeight = (px: number, py: number) => {
    const pxWrapped = ((px % width) + width) % width;
    const pyWrapped = ((py % height) + height) % height;
    return heightMap[pyWrapped * width + pxWrapped] ?? 0;
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const hL = getHeight(x - 1, y);
      const hR = getHeight(x + 1, y);
      const hD = getHeight(x, y - 1);
      const hU = getHeight(x, y + 1);

      // Tangent-space normal of a height field is (-dh/du, -dh/dv, 1). The
      // row index runs opposite to v because of flipY, so dy already carries
      // its sign; dx has to be negated explicitly or the two axes light from
      // opposite directions and a pore reads as a pimple half the time.
      const dx = (hL - hR) * intensity;
      const dy = (hU - hD) * intensity;
      const dz = Math.sqrt(Math.max(0, 1 - dx * dx - dy * dy));

      // Round, not floor: a flat texel is 0.5, and flooring encodes it as 127
      // instead of 128 — a constant -0.004 tilt across every unbroken face,
      // which the `normalScale` these maps are used at multiplies back up into
      // a visible wash of uneven shading.
      const index = (y * width + x) * 4;
      data[index] = Math.round((dx * 0.5 + 0.5) * 255);
      data[index + 1] = Math.round((dy * 0.5 + 0.5) * 255);
      data[index + 2] = Math.round((dz * 0.5 + 0.5) * 255);
      data[index + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  return createProceduralTexture(canvas);
}

/**
 * The extremes of a field, remembered against the field itself.
 *
 * One height field is baked into a relief, an ambient occlusion and a
 * roughness map, and each of those normalises to the field's own min/max — so
 * without this the same quarter-million texels are scanned three times over
 * for an answer that cannot have changed. Keyed weakly, so a field that goes
 * out of scope takes its range with it.
 *
 * The identity of the array is the cache key, so a field must be filled once
 * and then left alone. Refilling a scratch buffer and baking it again would
 * normalise the second field against the first one's extremes and silently
 * clamp everything outside them to black or white; allocate a new array per
 * field instead.
 */
const fieldRanges = new WeakMap<Float32Array, { min: number; max: number }>();

function fieldRange(field: Float32Array): { min: number; max: number } {
  const known = fieldRanges.get(field);
  if (known) return known;

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of field) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  const range = { min, max };
  fieldRanges.set(field, range);
  return range;
}

/**
 * Shared body of the three greyscale bakes below.
 *
 * All of them normalise the field to its own min/max and map that onto a
 * `[low, high]` ramp; only what the ramp means differs. A flat field (no
 * relief at all) normalises to 1, so it comes out at `high` — the untouched
 * face value — rather than dividing by zero.
 */
function fieldToGrayMap(
  label: string,
  extent: TextureExtent,
  field: Float32Array,
  low: number,
  high: number,
): THREE.CanvasTexture {
  const { width, height } = textureExtent(extent);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error(`Could not get canvas context for procedural ${label}-map`);
  }

  const { min, max } = fieldRange(field);
  const span = max - min;

  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;

  for (let i = 0; i < width * height; i++) {
    const normalized = span > 0 ? ((field[i] ?? 0) - min) / span : 1;
    const ramped = low + (high - low) * normalized;
    const value = Math.round(Math.min(1, Math.max(0, ramped)) * 255);

    const index = i * 4;
    data[index] = value;
    data[index + 1] = value;
    data[index + 2] = value;
    data[index + 3] = 255;
  }

  ctx.putImageData(imageData, 0, 0);

  return createProceduralTexture(canvas);
}

/**
 * Bake a field into a repeating colour map that tints whatever it multiplies.
 *
 * three multiplies `map` by `material.color`, so a near-white map shifts the
 * base colour slightly rather than replacing it — the way a veneer's figure
 * shows through a thin coating without changing what colour the panel is.
 *
 * @param low Multiplier where the field is lowest, `high` where it is highest.
 */
export function fieldToTintMap(
  size: number,
  field: Float32Array,
  low: number,
  high: number,
): THREE.CanvasTexture {
  const texture = fieldToGrayMap("tint", size, field, low, high);
  // Colour data, unlike the normal, roughness and AO maps.
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Bake a height field into a repeating ambient occlusion map.
 *
 * Image-based lighting arrives from every direction at once, so a recess is lit
 * exactly as brightly as the face around it. A pore or a groove needs that
 * contact darkening painted in, and unlike the screen-space AO pass this one
 * resolves detail far below a pixel.
 *
 * @param strength How dark the lowest point of the field goes; 0 disables it.
 */
export function heightMapToAoMap(
  size: number,
  heightMap: Float32Array,
  strength: number,
): THREE.CanvasTexture {
  // The face stays unoccluded and the deepest point drops by `strength`.
  return fieldToGrayMap("ao", size, heightMap, 1 - strength, 1);
}

/**
 * Bake a height field into a repeating greyscale copy of itself.
 *
 * The other bakes here turn a field into something a standard material can
 * consume directly. This one keeps the field as it is — white at the top of the
 * relief, black at the bottom of it — for a shader that has to know how deep a
 * point lies rather than how it is lit, which is what stepping a view ray into
 * the surface needs.
 */
export function heightMapToReliefMap(
  size: TextureExtent,
  heightMap: Float32Array,
): THREE.CanvasTexture {
  return fieldToGrayMap("relief", size, heightMap, 0, 1);
}

/**
 * Bake a height field into a repeating roughness map.
 *
 * Surfaces in the real world vary in gloss as much as they vary in shape:
 * the bottom of a groove or a leather crease is duller than the face around
 * it, because it collects the wear and the dirt. Without this every material
 * has one perfectly uniform micro-roughness, which is the strongest remaining
 * "computer graphics" tell after lighting.
 *
 * Values are absolute roughness, so the material using this map wants
 * `roughness = 1` — three multiplies the two together.
 *
 * @param low Roughness where the height field is at its lowest (in a groove).
 * @param high Roughness on the untouched face.
 */
export function heightMapToRoughnessMap(
  size: TextureExtent,
  heightMap: Float32Array,
  low: number,
  high: number,
): THREE.CanvasTexture {
  return fieldToGrayMap("roughness", size, heightMap, low, high);
}

/**
 * Pack pore AO (R), roughness (G) and normalized relief (B) into one texture.
 * Each channel keeps the independently rounded bytes of its grayscale bake;
 * deriving AO/roughness from the already-quantized relief would lose precision.
 * All three fields have the same coordinates, colour space and filtering.
 */
export function heightMapToAoRoughnessReliefMap(
  size: number,
  field: Float32Array,
  aoStrength: number,
  lowRoughness: number,
  highRoughness: number,
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context for procedural pore-map");
  }
  const { min, max } = fieldRange(field);
  const span = max - min;
  const lowAo = 1 - aoStrength;
  const image = ctx.createImageData(size, size);
  const byte = (value: number) =>
    Math.round(Math.min(1, Math.max(0, value)) * 255);
  for (let i = 0; i < size * size; i++) {
    const normalized = span > 0 ? ((field[i] ?? 0) - min) / span : 1;
    image.data[i * 4] = byte(lowAo + (1 - lowAo) * normalized);
    image.data[i * 4 + 1] = byte(
      lowRoughness + (highRoughness - lowRoughness) * normalized,
    );
    image.data[i * 4 + 2] = byte(normalized);
    image.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  return createProceduralTexture(canvas);
}

/**
 * Bake an analytic slope field into a repeating normal map.
 *
 * {@link heightMapToNormalMap} differences a sampled height field, which is
 * right for a field that only exists as samples. A surface that exists as a
 * function has an exact gradient, and encoding that directly avoids the two
 * ways the difference goes wrong: it treats the slope as the sine of the tilt
 * rather than its tangent, and clamps flat past 45°, where a nub flank is.
 *
 * @param slope Gradient of the height at a point `(u, v)` of the tile, as rise
 *   over run with both in the same unit — millimetres of height per millimetre
 *   along the surface. `v` runs upward in the image, as for the other bakes.
 */
export function slopeToNormalMap(
  size: number,
  slope: (u: number, v: number) => [number, number],
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context for procedural normal-map");
  }

  const imageData = ctx.createImageData(size, size);
  const data = imageData.data;

  for (let y = 0; y < size; y++) {
    // flipY: the top row of the canvas is v = 1.
    const v = 1 - (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      const [du, dv] = slope(u, v);
      // Tangent-space normal of a height field is (-dh/du, -dh/dv, 1).
      const length = Math.sqrt(du * du + dv * dv + 1);
      const index = (y * size + x) * 4;
      data[index] = Math.round((-du / length) * 127.5 + 127.5);
      data[index + 1] = Math.round((-dv / length) * 127.5 + 127.5);
      data[index + 2] = Math.round((1 / length) * 127.5 + 127.5);
      data[index + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  return createProceduralTexture(canvas);
}

/**
 * Bake a field of absolute values in [0, 1] into a repeating greyscale map.
 *
 * Unlike the ramped bakes above this does not normalise: an occlusion computed
 * from a surface already is the number the shader wants.
 */
export function valuesToGrayMap(
  size: number,
  values: Float32Array,
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not get canvas context for procedural value-map");
  }

  const imageData = ctx.createImageData(size, size);
  const data = imageData.data;

  for (let i = 0; i < size * size; i++) {
    const value = Math.round(Math.min(1, Math.max(0, values[i] ?? 1)) * 255);
    const index = i * 4;
    data[index] = value;
    data[index + 1] = value;
    data[index + 2] = value;
    data[index + 3] = 255;
  }

  ctx.putImageData(imageData, 0, 0);

  return createProceduralTexture(canvas);
}
